// Admin → Parameter sets: the published tight-binding parameter sets SKFitter's fits can start from (DFTB.org
// Slater-Koster tables, NIST atomic reference data, Harrison's universal formulas, NRL parameter files). Review the
// values (on-site energies, a pair's integrals at a distance), import source files, edit, switch sets off, restore
// earlier versions or the shipped one.
import { Alert, Anchor, Badge, Button, Card, Checkbox, Drawer, FileInput, Group, Modal, NumberInput, ScrollArea, Select,
  SimpleGrid, Stack, Switch, Table, Tabs, Text, TextInput, Textarea } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { ErrorBox, notifyError, notifyOk } from "../../components/Errors";
import { formatDate, get, patch, post } from "../../lib/api";

type Kind = "dftb" | "nist" | "harrison" | "nrl";
interface SetRow {
  id: string; name: string; kind: Kind; description?: string; source?: string; url?: string; licence?: string;
  citation?: string; version?: string; notes?: string; elements: string[]; enabled?: boolean;
  origin: "shipped" | "admin"; modified: boolean; updated?: string; updated_by?: string;
}
interface SetDetail extends SetRow {
  import_notes: string[]; onsite: Record<string, Partial<Record<Fam, number>>>;
  history: { version: string; updated?: string; updated_by?: string; elements?: string[]; import_notes: string[] }[];
  pairs?: string[]; grid?: [number, number, number];
  harrison?: { eta: Record<string, number>; hbar2_m: number; rd: Record<string, number> };
  nrl?: Record<string, { title: string; style: string; rcut_bohr: number; families: string[]; integrals: string[] }>;
  configurations?: Record<string, string>;
}
interface Preview {
  onsite: Record<string, Partial<Record<Fam, number | null>>>; onsite_note: string;
  integrals: { integral: string; H: number | null; S: number | null; eta: number | null }[];
}
type Fam = "s" | "p" | "d" | "f";
const FAMS: Fam[] = ["s", "p", "d", "f"];

export const KIND_NAME: Record<Kind, string> = {
  dftb: "DFTB Slater-Koster tables", nist: "NIST atomic data", harrison: "Harrison universal", nrl: "NRL tight-binding",
};
const IMPORT_HELP: Record<Kind, string> = {
  dftb: "Slater-Koster files named like C-H.skf (Hartree, bohr; e.g. a set from dftb.org), or a .zip of them. The pairs and on-site energies in the files are added or replace the ones in the set.",
  nist: "The NIST table in the text format of the shipped file nist_raw.txt: one line per element, 'Z|configuration|1s=LDA,ScRLDA;2s=...' (Hartree).",
  harrison: "A CSV table 'element,r_d' (Å) - it switches on Harrison's s-d, p-d and d-d integrals - optionally with term values: header 'element,r_d,e_s,e_p,e_d,e_f' (eV; they replace the NIST on-site energies).",
  nrl: "NRL tight-binding parameter files for single elements (e.g. si_par.spd from esd.cos.gmu.edu/tb), or a .zip of them.",
};
const IMPORT_ACCEPT: Record<Kind, string> = { dftb: ".skf,.zip", nist: ".txt,.dat", harrison: ".csv,.txt", nrl: "*" };

const fmt = (v: number | null | undefined, d = 3) => (v == null || !Number.isFinite(v) ? "–" : v.toFixed(d));

function readB64(file: File): Promise<{ name: string; data: string }> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve({ name: file.name, data: String(r.result).split(",", 2)[1] ?? "" });
    r.onerror = () => reject(r.error ?? new Error(`${file.name} could not be read`));
    r.readAsDataURL(file);
  });
}

export function ParamSetsTab() {
  const sets = useQuery({ queryKey: ["admin-paramsets"], queryFn: () => get<SetRow[]>("/admin/paramsets") });
  const [open, setOpen] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  return (
    <Card withBorder>
      <Group justify="space-between" mb="sm" align="flex-start">
        <Text size="sm" c="dimmed" maw={760}>
          The sets the Slater-Koster fit set-up offers under “Starting values and bounds from”. The shipped sets are read-only
          copies of published data; changes and imports make a new version (the previous one can be restored), and
          “Back to the shipped version” undoes them.
        </Text>
        <Button size="xs" onClick={() => setCreating(true)}>New set</Button>
      </Group>
      <ErrorBox error={sets.error} />
      <Table.ScrollContainer minWidth={700}>
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr><Table.Th>Set</Table.Th><Table.Th>Kind</Table.Th><Table.Th>Elements</Table.Th><Table.Th>Version</Table.Th>
              <Table.Th>Status</Table.Th><Table.Th>Changed</Table.Th></Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sets.data?.map((s) => (
              <Table.Tr key={s.id} style={{ cursor: "pointer" }} onClick={() => setOpen(s.id)}>
                <Table.Td><Text size="sm" fw={500}>{s.name}</Text><Text size="xs" c="dimmed" ff="monospace">{s.id}</Text></Table.Td>
                <Table.Td><Badge size="sm" variant="light">{KIND_NAME[s.kind] ?? s.kind}</Badge></Table.Td>
                <Table.Td><Text size="xs">{s.elements.length > 16 ? `${s.elements.length} elements (${s.elements[0]}–${s.elements[s.elements.length - 1]})` : s.elements.join(" ") || "–"}</Text></Table.Td>
                <Table.Td><Text size="xs">{s.version || "–"}</Text></Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <Badge size="xs" color={s.origin === "admin" ? "grape" : s.modified ? "orange" : "gray"} variant="light">
                      {s.origin === "admin" ? "added" : s.modified ? "changed" : "shipped"}</Badge>
                    {s.enabled === false && <Badge size="xs" color="red" variant="outline">off</Badge>}
                  </Group>
                </Table.Td>
                <Table.Td><Text size="xs">{s.updated ? `${formatDate(s.updated, true)} · ${s.updated_by ?? ""}` : "–"}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {open && <SetDrawer id={open} onClose={() => setOpen(null)} />}
      <NewSetModal opened={creating} onClose={() => setCreating(false)} sets={sets.data ?? []} onCreated={(id) => { setCreating(false); setOpen(id); }} />
    </Card>
  );
}

function useInvalidate() {
  const qc = useQueryClient();
  return (id: string, d?: SetDetail) => {
    if (d) qc.setQueryData(["admin-paramset", id], d);
    qc.invalidateQueries({ queryKey: ["admin-paramsets"] });
    qc.invalidateQueries({ queryKey: ["sk-paramsets"] });
  };
}

function SetDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useQuery({ queryKey: ["admin-paramset", id], queryFn: () => get<SetDetail>(`/admin/paramsets/${id}`) });
  const d = q.data;
  const [tab, setTab] = useState<string | null>("values");
  return (
    <Drawer opened onClose={onClose} position="right" size="xl" title={d ? d.name : "Parameter set"}>
      <ErrorBox error={q.error} />
      {d && (
        <Stack gap="sm">
          <Group gap={6}>
            <Badge variant="light">{KIND_NAME[d.kind]}</Badge>
            <Badge variant="light" color={d.origin === "admin" ? "grape" : d.modified ? "orange" : "gray"}>
              {d.origin === "admin" ? "added by an admin" : d.modified ? "changed from the shipped version" : "shipped version"}</Badge>
            <Text size="xs" c="dimmed">{d.elements.length} elements{d.updated ? ` · changed ${formatDate(d.updated, true)} by ${d.updated_by}` : ""}</Text>
          </Group>
          {d.import_notes.length > 0 && <Alert p="xs" color="blue" variant="light"><Text size="xs">{d.import_notes.join(" · ")}</Text></Alert>}
          <Tabs value={tab} onChange={setTab} keepMounted={false}>
            <Tabs.List>
              <Tabs.Tab value="values">Values</Tabs.Tab>
              <Tabs.Tab value="preview">{d.kind === "nist" ? "On-site check" : "Pair check"}</Tabs.Tab>
              <Tabs.Tab value="import">Import files</Tabs.Tab>
              <Tabs.Tab value="about">Description & licence</Tabs.Tab>
              <Tabs.Tab value="history">History ({d.history.length})</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="values" pt="sm"><ValuesPanel d={d} /></Tabs.Panel>
            <Tabs.Panel value="preview" pt="sm"><PreviewPanel d={d} /></Tabs.Panel>
            <Tabs.Panel value="import" pt="sm"><ImportPanel d={d} /></Tabs.Panel>
            <Tabs.Panel value="about" pt="sm"><AboutPanel d={d} /></Tabs.Panel>
            <Tabs.Panel value="history" pt="sm"><HistoryPanel d={d} onRemoved={onClose} /></Tabs.Panel>
          </Tabs>
        </Stack>
      )}
    </Drawer>
  );
}

function ValuesPanel({ d }: { d: SetDetail }) {
  const refresh = useInvalidate();
  const [edits, setEdits] = useState<Record<string, Record<string, string>>>({});
  const [filter, setFilter] = useState("");
  useEffect(() => setEdits({}), [d]);
  const changes = useMemo(() => {
    const out: Record<string, Record<string, number | null>> = {};
    for (const [el, fams] of Object.entries(edits)) {
      for (const [f, t] of Object.entries(fams)) {
        const v = t.trim() === "" ? null : Number(t);
        if (v !== null && !Number.isFinite(v)) continue;
        if (v === (d.onsite[el]?.[f as Fam] ?? null)) continue;
        (out[el] ??= {})[f] = v;
      }
    }
    return out;
  }, [edits, d]);
  const n = Object.values(changes).reduce((a, x) => a + Object.keys(x).length, 0);
  const save = useMutation({
    mutationFn: () => patch<SetDetail>(`/admin/paramsets/${d.id}`, { onsite: changes }),
    onSuccess: (x) => { refresh(d.id, x); notifyOk("On-site energies saved (the previous version is in the history)."); },
    onError: (e) => notifyError(e),
  });
  const els = Object.keys(d.onsite).filter((e) => !filter || e.toLowerCase().startsWith(filter.toLowerCase()));
  const editable = d.kind !== "nrl";
  return (
    <Stack gap="sm">
      {d.kind === "nrl" ? (
        <NrlTable d={d} />
      ) : (
        <>
          <Group justify="space-between">
            <Text size="sm" fw={500}>On-site energies (eV{d.kind === "dftb" ? ", free atom" : ", neutral atom"})</Text>
            <TextInput size="xs" placeholder="Filter element" value={filter} onChange={(e) => setFilter(e.currentTarget.value)} w={140} />
          </Group>
          <ScrollArea.Autosize mah={360}>
            <Table withTableBorder verticalSpacing={2} stickyHeader>
              <Table.Thead><Table.Tr><Table.Th>Element</Table.Th>{FAMS.map((f) => <Table.Th key={f}>{f}</Table.Th>)}
                {d.configurations && <Table.Th>Configuration</Table.Th>}</Table.Tr></Table.Thead>
              <Table.Tbody>
                {els.map((el) => (
                  <Table.Tr key={el}>
                    <Table.Td><Text size="sm" fw={500}>{el}</Text></Table.Td>
                    {FAMS.map((f) => (
                      <Table.Td key={f}>
                        <TextInput size="xs" w={90} aria-label={`${el} ${f}`} disabled={!editable}
                          value={edits[el]?.[f] ?? (d.onsite[el]?.[f] != null ? String(d.onsite[el]![f]) : "")}
                          onChange={(e) => { const t = e.currentTarget.value; setEdits((o) => ({ ...o, [el]: { ...(o[el] ?? {}), [f]: t } })); }} />
                      </Table.Td>
                    ))}
                    {d.configurations && <Table.Td><Text size="xs">{d.configurations[el] ?? ""}</Text></Table.Td>}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
          <Group justify="flex-end">
            <Button size="xs" variant="default" disabled={!n} onClick={() => setEdits({})}>Discard</Button>
            <Button size="xs" disabled={!n} loading={save.isPending} onClick={() => save.mutate()}>Save {n ? `(${n} changes)` : ""}</Button>
          </Group>
        </>
      )}
      {d.kind === "dftb" && (
        <div>
          <Text size="sm" fw={500} mb={4}>Pair tables ({d.pairs?.length ?? 0}){d.grid ? ` · ${d.grid[0]}–${(d.grid[0] + d.grid[1] * (d.grid[2] - 1)).toFixed(2)} Å` : ""}</Text>
          <Group gap={4}>{d.pairs?.map((p) => <Badge key={p} size="xs" variant="outline" color="gray">{p}</Badge>)}</Group>
        </div>
      )}
      {d.kind === "harrison" && d.harrison && <HarrisonTables d={d} />}
    </Stack>
  );
}

function NrlTable({ d }: { d: SetDetail }) {
  const rows = Object.entries(d.nrl ?? {});
  if (!rows.length) return <Text size="sm" c="dimmed">No NRL parameter files imported yet - use “Import files”.</Text>;
  return (
    <Table withTableBorder>
      <Table.Thead><Table.Tr><Table.Th>Element</Table.Th><Table.Th>File</Table.Th><Table.Th>Cut-off</Table.Th><Table.Th>On-site</Table.Th><Table.Th>Integrals</Table.Th></Table.Tr></Table.Thead>
      <Table.Tbody>
        {rows.map(([el, e]) => (
          <Table.Tr key={el}>
            <Table.Td>{el}</Table.Td><Table.Td><Text size="xs">{e.title} ({e.style})</Text></Table.Td>
            <Table.Td><Text size="xs">{e.rcut_bohr} bohr</Text></Table.Td>
            <Table.Td><Text size="xs">{e.families.join(", ")}</Text></Table.Td><Table.Td><Text size="xs">{e.integrals.join(" ")}</Text></Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function HarrisonTables({ d }: { d: SetDetail }) {
  const refresh = useInvalidate();
  const h = d.harrison!;
  const [el, setEl] = useState("");
  const [rd, setRd] = useState<number | string>("");
  const save = useMutation({
    mutationFn: (body: Record<string, number | null>) => patch<SetDetail>(`/admin/paramsets/${d.id}`, { rd: body }),
    onSuccess: (x) => { refresh(d.id, x); setEl(""); setRd(""); },
    onError: (e) => notifyError(e),
  });
  return (
    <SimpleGrid cols={{ base: 1, md: 2 }}>
      <div>
        <Text size="sm" fw={500}>Coefficients η (V = η ħ²/(m d²), ħ²/m = {h.hbar2_m} eV Å²)</Text>
        <Table withTableBorder verticalSpacing={1}>
          <Table.Tbody>
            {Object.entries(h.eta).map(([k, v]) => <Table.Tr key={k}><Table.Td ff="monospace">{k}</Table.Td><Table.Td>{v}</Table.Td></Table.Tr>)}
          </Table.Tbody>
        </Table>
      </div>
      <div>
        <Text size="sm" fw={500}>r_d (Å) for the d couplings</Text>
        {Object.keys(h.rd).length === 0 && <Text size="xs" c="dimmed">None yet: import a CSV table or add values here.</Text>}
        <Table withTableBorder verticalSpacing={1}>
          <Table.Tbody>
            {Object.entries(h.rd).map(([k, v]) => (
              <Table.Tr key={k}><Table.Td>{k}</Table.Td><Table.Td>{v}</Table.Td>
                <Table.Td><Button size="compact-xs" variant="subtle" color="red" onClick={() => save.mutate({ [k]: null })}>remove</Button></Table.Td></Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <Group gap="xs" mt="xs" align="flex-end">
          <TextInput size="xs" label="Element" w={80} value={el} onChange={(e) => setEl(e.currentTarget.value)} />
          <NumberInput size="xs" label="r_d (Å)" w={100} value={rd} onChange={setRd} decimalScale={4} min={0.05} max={5} />
          <Button size="xs" disabled={!el || typeof rd !== "number"} loading={save.isPending}
            onClick={() => save.mutate({ [el.trim()]: Number(rd) })}>Add / change</Button>
        </Group>
      </div>
    </SimpleGrid>
  );
}

function PreviewPanel({ d }: { d: SetDetail }) {
  const els = d.kind === "nrl" ? Object.keys(d.nrl ?? {}) : d.elements;
  const first = d.pairs?.find((p) => p.split("-")[0] !== "H")?.split("-") ?? [els.find((e) => e !== "H") ?? els[0], els.find((e) => e !== "H") ?? els[0]];
  const [a, setA] = useState<string | null>(first[0] ?? null);
  const [b, setB] = useState<string | null>(first[1] ?? null);
  const [r, setR] = useState<number | string>(2.35);
  const ok = !!a && !!b && typeof r === "number";
  const q = useQuery({
    queryKey: ["admin-paramset-preview", d.id, d.updated, a, b, r],
    queryFn: () => get<Preview>(`/admin/paramsets/${d.id}/preview?a=${a}&b=${b}&r=${r}`),
    enabled: ok,
  });
  const p = q.data;
  return (
    <Stack gap="sm">
      <Group align="flex-end">
        <Select size="xs" label="Atom 1" data={els} value={a} onChange={setA} searchable w={110} allowDeselect={false} />
        <Select size="xs" label="Atom 2" data={els} value={b} onChange={setB} searchable w={110} allowDeselect={false} />
        <NumberInput size="xs" label="Distance (Å)" value={r} onChange={setR} min={0.5} max={10} step={0.05} decimalScale={3} w={120} />
      </Group>
      <ErrorBox error={q.error} />
      {p && (
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          <div>
            <Text size="sm" fw={500}>On-site energies (eV)</Text>
            <Table withTableBorder verticalSpacing={1}>
              <Table.Thead><Table.Tr><Table.Th /><Table.Th>s</Table.Th><Table.Th>p</Table.Th><Table.Th>d</Table.Th><Table.Th>f</Table.Th></Table.Tr></Table.Thead>
              <Table.Tbody>
                {Object.entries(p.onsite).map(([el, v]) => (
                  <Table.Tr key={el}><Table.Td>{el}</Table.Td>{FAMS.map((f) => <Table.Td key={f}>{fmt(v[f])}</Table.Td>)}</Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {p.onsite_note && <Text size="xs" c="dimmed">{p.onsite_note}</Text>}
          </div>
          <div>
            <Text size="sm" fw={500}>Two-centre integrals at {r} Å (first orbital on atom 1)</Text>
            {p.integrals.length === 0 ? <Text size="xs" c="dimmed">{d.kind === "nist" ? "This set has on-site energies only." : "No integrals for this pair at this distance."}</Text> : (
              <Table withTableBorder verticalSpacing={1}>
                <Table.Thead><Table.Tr><Table.Th>Integral</Table.Th><Table.Th>H (eV)</Table.Th><Table.Th>S</Table.Th><Table.Th>η = −dlnV/dlnr</Table.Th></Table.Tr></Table.Thead>
                <Table.Tbody>
                  {p.integrals.map((x) => (
                    <Table.Tr key={x.integral}><Table.Td ff="monospace">{x.integral}</Table.Td><Table.Td>{fmt(x.H)}</Table.Td>
                      <Table.Td>{fmt(x.S, 4)}</Table.Td><Table.Td>{fmt(x.eta, 2)}</Table.Td></Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </div>
        </SimpleGrid>
      )}
    </Stack>
  );
}

function ImportPanel({ d }: { d: SetDetail }) {
  const refresh = useInvalidate();
  const [files, setFiles] = useState<File[]>([]);
  const [replace, setReplace] = useState(false);
  const m = useMutation({
    mutationFn: async () => post<SetDetail>(`/admin/paramsets/${d.id}/import`, { files: await Promise.all(files.map(readB64)), replace }),
    onSuccess: (x) => { refresh(d.id, x); setFiles([]); notifyOk(`Imported: ${x.import_notes.join(" · ")}`); },
  });
  return (
    <Stack gap="sm">
      <Text size="sm">{IMPORT_HELP[d.kind]}</Text>
      <FileInput multiple clearable label="Files" placeholder="Choose files" value={files} onChange={setFiles} accept={IMPORT_ACCEPT[d.kind]} />
      <Checkbox label="Replace the whole set (otherwise the files add to it / replace the same entries)" checked={replace}
        onChange={(e) => setReplace(e.currentTarget.checked)} />
      <ErrorBox error={m.error} />
      <Group justify="flex-end">
        <Button disabled={!files.length} loading={m.isPending} onClick={() => m.mutate()}>Check and import</Button>
      </Group>
      <Text size="xs" c="dimmed">The files are checked and converted (units, signs) before anything changes; the current version stays in the history.</Text>
    </Stack>
  );
}

function AboutPanel({ d }: { d: SetDetail }) {
  const refresh = useInvalidate();
  const fields = ["name", "description", "source", "url", "licence", "citation", "version", "notes"] as const;
  const [v, setV] = useState<Record<string, string>>({});
  useEffect(() => setV(Object.fromEntries(fields.map((f) => [f, (d[f] as string) ?? ""]))), [d]);
  const changed = Object.fromEntries(fields.filter((f) => (v[f] ?? "") !== ((d[f] as string) ?? "")).map((f) => [f, v[f]]));
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => patch<SetDetail>(`/admin/paramsets/${d.id}`, body),
    onSuccess: (x) => { refresh(d.id, x); notifyOk("Saved."); },
    onError: (e) => notifyError(e),
  });
  const area = (f: (typeof fields)[number], label: string) => (
    <Textarea size="xs" label={label} autosize minRows={1} maxRows={6} value={v[f] ?? ""} onChange={(e) => { const t = e.currentTarget.value; setV((o) => ({ ...o, [f]: t })); }} />
  );
  return (
    <Stack gap="xs">
      <Switch label="Offered in the fit set-up" checked={d.enabled !== false} onChange={(e) => save.mutate({ enabled: e.currentTarget.checked })} />
      <TextInput size="xs" label="Name" value={v.name ?? ""} onChange={(e) => { const t = e.currentTarget.value; setV((o) => ({ ...o, name: t })); }} />
      {area("description", "Description (shown to users)")}
      <SimpleGrid cols={2}>
        <TextInput size="xs" label="Source" value={v.source ?? ""} onChange={(e) => { const t = e.currentTarget.value; setV((o) => ({ ...o, source: t })); }} />
        <TextInput size="xs" label="Version" value={v.version ?? ""} onChange={(e) => { const t = e.currentTarget.value; setV((o) => ({ ...o, version: t })); }} />
      </SimpleGrid>
      <TextInput size="xs" label="Link" value={v.url ?? ""} onChange={(e) => { const t = e.currentTarget.value; setV((o) => ({ ...o, url: t })); }} />
      {area("licence", "Licence")}
      {area("citation", "Please cite (shown to users when the set is used)")}
      {area("notes", "Notes")}
      {d.url && <Anchor size="xs" href={d.url} target="_blank" rel="noreferrer">{d.url}</Anchor>}
      <Group justify="flex-end">
        <Button size="xs" disabled={!Object.keys(changed).length} loading={save.isPending} onClick={() => save.mutate(changed)}>Save</Button>
      </Group>
    </Stack>
  );
}

function HistoryPanel({ d, onRemoved }: { d: SetDetail; onRemoved: () => void }) {
  const refresh = useInvalidate();
  const restore = useMutation({
    mutationFn: (version: string) => post<SetDetail>(`/admin/paramsets/${d.id}/restore`, { version }),
    onSuccess: (x) => { refresh(d.id, x); notifyOk("Version restored."); },
    onError: (e) => notifyError(e),
  });
  const revert = useMutation({
    mutationFn: () => post<SetDetail | { removed: true }>(`/admin/paramsets/${d.id}/revert`),
    onSuccess: (x) => {
      if ("removed" in x) { refresh(d.id); onRemoved(); notifyOk("Set removed (it stays in the history on the server)."); }
      else { refresh(d.id, x as SetDetail); notifyOk("Back to the shipped version."); }
    },
    onError: (e) => notifyError(e),
  });
  const canRevert = d.origin === "admin" || d.modified;
  return (
    <Stack gap="sm">
      {d.history.length === 0 ? <Text size="sm" c="dimmed">No earlier versions.</Text> : (
        <Table withTableBorder>
          <Table.Thead><Table.Tr><Table.Th>Replaced version of</Table.Th><Table.Th>By</Table.Th><Table.Th>What</Table.Th><Table.Th /></Table.Tr></Table.Thead>
          <Table.Tbody>
            {d.history.map((h) => (
              <Table.Tr key={h.version}>
                <Table.Td><Text size="xs">{h.updated ? formatDate(h.updated, true) : h.version}</Text></Table.Td>
                <Table.Td><Text size="xs">{h.updated_by ?? ""}</Text></Table.Td>
                <Table.Td><Text size="xs">{h.import_notes.join(" · ")}</Text></Table.Td>
                <Table.Td><Button size="compact-xs" variant="light" loading={restore.isPending} onClick={() => restore.mutate(h.version)}>Restore</Button></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      {canRevert && (
        <Group justify="flex-end">
          <Button color="red" variant="light" size="xs" loading={revert.isPending} onClick={() => revert.mutate()}>
            {d.origin === "admin" ? "Remove this set" : "Back to the shipped version"}</Button>
        </Group>
      )}
    </Stack>
  );
}

function NewSetModal({ opened, onClose, sets, onCreated }: { opened: boolean; onClose: () => void; sets: SetRow[]; onCreated: (id: string) => void }) {
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<Kind>("dftb");
  const [copy, setCopy] = useState<string | null>(null);
  const refresh = useInvalidate();
  const m = useMutation({
    mutationFn: () => post<SetDetail>("/admin/paramsets", { id, name, kind, copy_of: copy }),
    onSuccess: (x) => { refresh(x.id, x); onCreated(x.id); setId(""); setName(""); setCopy(null); },
  });
  return (
    <Modal opened={opened} onClose={onClose} title="New parameter set" centered>
      <Stack>
        <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} placeholder="e.g. DFTB.org transition metals (trans3d)" />
        <TextInput label="Id" description="lower case letters, digits and -" value={id} onChange={(e) => setId(e.currentTarget.value.toLowerCase())} />
        <Select label="Kind" value={kind} onChange={(v) => { if (v) { setKind(v as Kind); setCopy(null); } }} allowDeselect={false}
          data={(Object.keys(KIND_NAME) as Kind[]).map((k) => ({ value: k, label: KIND_NAME[k] }))} />
        <Select label="Start from" placeholder="an empty set" clearable value={copy} onChange={setCopy}
          data={sets.filter((s) => s.kind === kind).map((s) => ({ value: s.id, label: s.name }))} />
        <ErrorBox error={m.error} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button onClick={() => m.mutate()} loading={m.isPending} disabled={!id || !name}>Create</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
